import { Link } from '@tanstack/react-router';
import { postMeta } from './manifest';
import type { Post } from './types';
import { Callout, H2, OL, P, UL } from './ui';

const post: Post = { meta: postMeta('how-to-make-a-trap-beat-in-30-minutes'), Body };
export default post;

function Body() {
  return (
    <>
      <P>
        This is the no-fluff version. 30 minutes total, free DAW, ready to submit to a beat battle
        when you are done. Skip the philosophical "what is trap" intros - if you are here, you
        already know.
      </P>

      <Callout>
        We use FL Studio's free trial for this guide because the channel rack is the fastest place
        to learn trap drum programming. Cakewalk by BandLab works too with minor adjustments.
      </Callout>

      <H2 id="step-1">Step 1 - Set the tempo (1 minute)</H2>
      <P>
        Open the DAW. Set the project tempo to <strong>140 BPM</strong>. Almost all modern trap sits
        between 130 and 160 BPM; 140 is the sweet spot and the easiest pocket to write in.
      </P>

      <H2 id="step-2">Step 2 - Drum backbone (5 minutes)</H2>
      <P>
        Trap drums are kick + snare + hi-hat, with an 808 doing the bass. Here is the simplest
        usable pattern:
      </P>
      <UL>
        <li>
          <strong>Kick</strong>: beat 1 and 3 of every bar (steps 1 and 9 in a 16-step grid).
        </li>
        <li>
          <strong>Snare/clap</strong>: beat 3 only (step 9). Layer a clap with the snare for weight.
        </li>
        <li>
          <strong>Hi-hat</strong>: every 8th note (steps 1, 3, 5, 7, 9, 11, 13, 15).
        </li>
      </UL>
      <P>
        Loop it. Already sounds like trap. Now make it feel less robotic by humanizing the hats:
        randomize velocity slightly, push some hits 1-2 ticks late.
      </P>

      <H2 id="step-3">Step 3 - Hi-hat rolls and triplets (3 minutes)</H2>
      <P>
        Triplet rolls are the trap signature. Pick one bar in every four and replace half the hats
        with a fast 32nd-note roll on the same hat sample. Vary the velocity (low at start, high at
        the peak) so the roll has an arc. One roll per four bars is enough; more makes it cluttered.
      </P>

      <H2 id="step-4">Step 4 - The 808 (5 minutes)</H2>
      <P>
        The 808 is the bassline and the kick reinforcement at the same time. Pick a key (C minor is
        the path of least resistance). Lay an 808 sub on the root note, locked to the kick on beats
        1 and 3. Then move the 808 around to follow your future melody - one or two pitch changes
        per bar.
      </P>
      <P>Quick 808 mix:</P>
      <UL>
        <li>
          <strong>Volume</strong>: -8 dB on the channel fader. Trap 808s are LOUD.
        </li>
        <li>
          <strong>Saturation</strong>: a touch of soft clipping or tube saturation makes it audible
          on phone speakers.
        </li>
        <li>
          <strong>Glide</strong>: enable portamento (~50ms) on the 808 synth so pitch changes slide.
        </li>
        <li>
          <strong>Sidechain</strong>: light sidechain to the kick (4:1 ratio, 30ms attack, 80ms
          release) so the kick punches through.
        </li>
      </UL>

      <H2 id="step-5">Step 5 - Melody and chords (8 minutes)</H2>
      <P>
        Pick a melodic instrument from the kit - bell, pluck, piano, or pad. Write a 4-bar melodic
        loop in C minor. Two rules:
      </P>
      <OL>
        <li>Stay in scale (C, D, Eb, F, G, Ab, Bb).</li>
        <li>Use a sparse rhythm. 4 to 8 notes per bar, not 16.</li>
      </OL>
      <P>
        For chords, just play the root, third, and fifth of the same key on a soft pad. Make it
        quiet - the melody and 808 are the stars.
      </P>

      <H2 id="step-6">Step 6 - Arrangement (5 minutes)</H2>
      <P>Battle-length arrangement, total 90 seconds:</P>
      <OL>
        <li>0-8s: melody only, drums coming in (no kick yet)</li>
        <li>8-32s: full drop - drums + 808 + melody all together</li>
        <li>32-48s: breakdown - drop the kick, keep hats and melody, build tension</li>
        <li>48-80s: full drop again, add a vocal chop or new layer</li>
        <li>80-90s: outro tail - 808 fade or hard stop</li>
      </OL>

      <H2 id="step-7">Step 7 - Mix and master (3 minutes)</H2>
      <UL>
        <li>Pull all levels down so the master peaks at -6 dB.</li>
        <li>Put a stock limiter on the master with -1.0 dB ceiling and 6 dB of gain reduction.</li>
        <li>Aim for -8 LUFS integrated. Loud, but not crushed.</li>
        <li>Listen on phone speakers. If kick or 808 disappears, fix the EQ.</li>
      </UL>

      <H2 id="step-8">Step 8 - Export and submit</H2>
      <P>
        Export as WAV (44.1kHz, 16-bit) or 320kbps MP3. Both are accepted on most battle platforms.
        Now submit it to a trap battle.
      </P>
      <P>
        Pick a quickplay trap match on{' '}
        <Link to="/play" className="underline decoration-primary/50 underline-offset-2">
          Prod Battle
        </Link>{' '}
        and you can ship this beat in your next round. The constraint of a timer plus a shared kit
        is the fastest way to find out which of these steps you can do in 5 minutes and which one
        eats 15.
      </P>
    </>
  );
}
